import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { X, BookOpen, Plus, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { cn } from "@/lib/cn"
import type { Skill } from "@/types/skill"

// Reusable skills list — used inside SelectedAgentPanel (view mode) and on
// the agent edit page (edit mode). Edit mode renders attach/detach UI:
// each row exposes a Test Lab deep-link + an X icon to detach, and an
// "Attach skill" button opens a picker over the company's other skills
// not yet attached. Filtered by a typeable search box (case-insensitive
// match against name or description).
//
// Library imports happen elsewhere — operators import a starter skill on
// `/skills?scope=global`, which adds a per-company clone. The clone shows
// up in `availableSkills` here so operators can attach it.
type Props = {
  skills: Skill[]
  availableSkills?: Skill[]      // edit mode — defensive default
  agentId?: number               // edit mode — required to PATCH
  mode?: "view" | "edit"
  onManage?: () => void          // view mode — opens the edit page
}

export function AgentSkillsPanel({
  skills,
  availableSkills = [],
  agentId,
  mode = "view",
  onManage,
}: Props) {
  const [attachOpen, setAttachOpen] = useState(false)
  const [picked, setPicked]         = useState<number[]>([])
  const [search, setSearch]         = useState("")
  const [submitting, setSubmitting] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return availableSkills
    return availableSkills.filter((s) => {
      const name = (s.name ?? "").toLowerCase()
      const desc = (s.description ?? "").toLowerCase()
      const key  = (s.key ?? "").toLowerCase()
      return name.includes(q) || desc.includes(q) || key.includes(q)
    })
  }, [availableSkills, search])

  function detach(skillId: number) {
    if (!agentId || submitting) return
    setSubmitting(true)
    const next = skills.map((s) => s.id).filter((id) => id !== skillId)
    router.patch(
      `/agents/${agentId}/skills`,
      { skill_ids: next },
      {
        preserveScroll: false,
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function togglePicked(id: number) {
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
  }

  function closeAttach() {
    if (submitting) return
    setPicked([])
    setSearch("")
    setAttachOpen(false)
  }

  function submitAttach() {
    if (!agentId || picked.length === 0 || submitting) return
    setSubmitting(true)
    const next = Array.from(new Set([...skills.map((s) => s.id), ...picked]))
    router.patch(
      `/agents/${agentId}/skills`,
      { skill_ids: next },
      {
        preserveScroll: false,
        onFinish: () => {
          setSubmitting(false)
          setPicked([])
          setSearch("")
          setAttachOpen(false)
        },
      },
    )
  }

  return (
    <div>
      <header className="mb-3 flex items-center justify-between">
        <div className="font-medium">Assigned skills</div>
        {mode === "view" && onManage && (
          <button
            type="button"
            onClick={onManage}
            className="text-xs text-vip-gold transition hover:text-vip-goldSoft"
          >
            Manage
          </button>
        )}
        {mode === "edit" && (
          <Button
            variant="red"
            size="sm"
            onClick={() => setAttachOpen(true)}
            disabled={submitting}
            className="gap-1.5"
          >
            <Plus size={14} />
            Attach skill
          </Button>
        )}
      </header>

      {mode === "view" ? (
        skills.length === 0 ? (
          <Card variant="inset" className="p-4 text-sm text-app-muted">
            No skills assigned yet.
          </Card>
        ) : (
          <div className="space-y-2">
            {skills.map((s) => (
              <Card
                key={s.id}
                variant="inset"
                className="flex items-center justify-between p-3 text-sm"
              >
                <span>{s.name}</span>
                <span className="text-vip-gold">
                  {s.status === "live" ? "Live" : s.status === "draft" ? "Draft" : "Archived"}
                </span>
              </Card>
            ))}
          </div>
        )
      ) : skills.length === 0 ? (
        <Card variant="inset" className="p-4 text-sm text-app-muted">
          No skills attached yet. Click <span className="font-semibold text-app-text">Attach skill</span> to add one from your company's skills, or import a starter from the{" "}
          <Link href="/skills?scope=global" className="text-vip-gold hover:underline">Library</Link>.
        </Card>
      ) : (
        <div className="space-y-2">
          {skills.map((s) => (
            <Card
              key={s.id}
              variant="inset"
              className="flex items-center justify-between gap-3 p-3 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-app-text">{s.name}</span>
                  <code className="text-xs text-app-muted">{s.key}</code>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <Badge tone="neutral">{s.versionLabel}</Badge>
                {s.kind === "library" && <Badge tone="gold">Library</Badge>}
                <Link
                  href={`/skills/${s.id}/test`}
                  className="inline-flex items-center gap-1 text-xs text-app-muted transition hover:text-app-text"
                >
                  <BookOpen size={14} />
                  Open in Test Lab
                </Link>
                <button
                  type="button"
                  title="Detach skill"
                  aria-label="Detach skill"
                  onClick={() => detach(s.id)}
                  disabled={submitting}
                  className="rounded-control p-1 text-app-subtle transition hover:bg-white/5 hover:text-app-text disabled:opacity-50"
                >
                  <X size={14} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {mode === "edit" && (
        <Dialog open={attachOpen} onClose={closeAttach} size="md">
          <DialogHeader
            title="Attach a skill to this agent"
            subtitle={
              <span>
                Pick from your company's skills not yet attached. To bring in a starter skill, import it from the Library first.{" "}
                <InfoTooltip side="bottom">
                  Imports happen on the Library page. Open Skills → Library, click <strong>Import</strong> on the skill you want, then come back here to attach the imported copy.
                </InfoTooltip>
              </span>
            }
          />
          <DialogBody>
            <label className="flex items-center gap-2 rounded-control border border-border bg-white/[0.03] px-3 py-2">
              <Search size={14} className="text-app-subtle" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, description, or key…"
                className="flex-1 bg-transparent text-sm text-app-text placeholder:text-app-subtle focus:outline-none"
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-[11px] text-app-subtle hover:text-app-text"
                >
                  clear
                </button>
              )}
            </label>

            {availableSkills.length === 0 ? (
              <Card variant="inset" className="p-4 text-sm text-app-muted">
                <span className="font-semibold text-app-text">No skills left to attach.</span>{" "}
                Import a starter skill from the{" "}
                <Link href="/skills?scope=global" className="text-vip-gold hover:underline">Library</Link>{" "}
                first, or create a new one from the{" "}
                <Link href="/skills" className="text-vip-gold hover:underline">Skills</Link>{" "}
                page.
              </Card>
            ) : filtered.length === 0 ? (
              <Card variant="inset" className="p-4 text-sm text-app-muted">
                No matches for <code className="text-app-text">{search}</code>.
              </Card>
            ) : (
              <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                {filtered.map((s) => {
                  const checked = picked.includes(s.id)
                  return (
                    <label
                      key={s.id}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-control border px-3 py-2 text-sm transition",
                        checked
                          ? "border-brand-red/40 bg-brand-redSoft"
                          : "border-border bg-white/[0.03] hover:bg-white/[0.06]",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-brand-red"
                        checked={checked}
                        onChange={() => togglePicked(s.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-app-text">{s.name}</span>
                          <code className="text-xs text-app-muted">{s.key}</code>
                          {s.kind === "library" && <Badge tone="gold">Library</Badge>}
                        </span>
                        {s.description && (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-app-muted">
                            {s.description}
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}

          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={closeAttach} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="red"
              size="sm"
              disabled={picked.length === 0 || submitting}
              onClick={submitAttach}
            >
              Attach ({picked.length})
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  )
}
